import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { PageState } from '../components/ui';
import { reportClientError } from './report-client-error';

/**
 * `errorElement` for the router: replaces React Router's default developer
 * "Unexpected Application Error" screen with a friendly message, and reports
 * the crash to `error_logs`. The returned correlation id is shown as a
 * reference the user can quote to support - the platform admin can search
 * the Error Logs page by it.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const [reference, setReference] = useState<string | null>(null);

  useEffect(() => {
    if (isRouteErrorResponse(error)) return; // a 404/redirect-style response, not a crash
    let cancelled = false;
    void reportClientError(error, { kind: 'boundary' }).then((id) => {
      if (!cancelled) setReference(id);
    });
    return () => {
      cancelled = true;
    };
  }, [error]);

  if (isRouteErrorResponse(error)) {
    return <PageState kind="error" title={`${error.status} ${error.statusText}`} />;
  }

  return (
    <PageState
      kind="error"
      title="Something went wrong on this page"
      message={
        <>
          The problem has been reported automatically.
          {reference ? (
            <>
              {' '}
              Reference: <code>{reference}</code>
            </>
          ) : null}
        </>
      }
      onRetry={() => window.location.reload()}
    />
  );
}
